import { useEffect, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../store/store';
import { updateBalance } from '../store/slices/authSlice';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

/**
 * Custom hook to track user activity and keep session alive
 * Sends periodic heartbeat to backend to update lastActive timestamp
 */
export const useActivityTracker = () => {
  const dispatch = useDispatch();
  const { isConnected, token, user } = useSelector((state: RootState) => state.auth);
  const lastActivityRef = useRef<number>(Date.now());
  const heartbeatIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const lastBalanceRef = useRef<number>(user?.balance || 0);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Send heartbeat to backend to update lastActive
  const sendHeartbeat = useCallback(async () => {
    if (!isConnected || !token) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/heartbeat`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          lastActivity: new Date(lastActivityRef.current).toISOString(),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        // Update balance if it changed
        if (data.balance !== undefined) {
          const newBalance = Number(data.balance); // Convert string to number
          
          // Only show deposit notification from WebSocket, not heartbeat
          // Heartbeat is just for keeping session alive and syncing balance
          
          lastBalanceRef.current = newBalance;
          dispatch(updateBalance(newBalance));
        }
      }
    } catch (error) {
      console.error('Heartbeat error:', error);
      // Don't logout on heartbeat failure - could be temporary network issue
    }
  }, [isConnected, token, dispatch]);

  // Update lastBalanceRef when user balance changes externally
  useEffect(() => {
    if (user?.balance !== undefined) {
      lastBalanceRef.current = user.balance;
    }
  }, [user?.balance]);

  // Track user activity events
  useEffect(() => {
    if (!isConnected) {
      return;
    }

    const events = [
      'mousedown',
      'mousemove',
      'keypress',
      'scroll',
      'touchstart',
      'click',
    ];

    // Throttle activity updates to avoid excessive calls
    let throttleTimeout: NodeJS.Timeout | null = null;
    const throttledUpdate = () => {
      if (!throttleTimeout) {
        throttleTimeout = setTimeout(() => {
          updateActivity();
          throttleTimeout = null;
        }, 1000); // Update at most once per second
      }
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, throttledUpdate);
    });

    // Cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, throttledUpdate);
      });
      if (throttleTimeout) {
        clearTimeout(throttleTimeout);
      }
    };
  }, [isConnected, updateActivity]);

  // Send periodic heartbeat to backend
  useEffect(() => {
    if (!isConnected || !token) {
      // Clear interval if disconnected
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      return;
    }

    // Send heartbeat every 30 minutes to reduce server load and avoid rate limiting
    heartbeatIntervalRef.current = setInterval(() => {
      sendHeartbeat();
    }, 30 * 60 * 1000); // 30 minutes

    // Send initial heartbeat after 5 seconds to avoid startup rate limits
    const initialTimeout = setTimeout(() => {
      sendHeartbeat();
    }, 5000);

    // Cleanup
    return () => {
      if (heartbeatIntervalRef.current) {
        clearInterval(heartbeatIntervalRef.current);
        heartbeatIntervalRef.current = null;
      }
      clearTimeout(initialTimeout);
    };
  }, [isConnected, token, sendHeartbeat]);
};
